<template>
  <v-card
    v-if="currentChartDetails.chart"
    :id="`chart-details-${chartIndex}-${viewIndex}`"
    class="chartdetailsmenu resizable-card-content"
    :style="'min-width:400px; min-height:380px'"
  >
    <v-row :id="`details-header-${chartIndex}-${viewIndex}`" class="x-btn-row mr-n6">
      <v-spacer></v-spacer>
      <v-btn
        class="pr-0"
        size="small"
        color="grey-dark"
        variant="text"
        icon="mdi-open-in-new"
        @click="openDetailsInNewTab()"
      ></v-btn
      ><v-btn
        class=""
        size="small"
        color="grey-dark"
        variant="text"
        icon="mdi-close"
        @click="closeChartDetails()"
      ></v-btn
    ></v-row>
    <v-card-title id="chart_details_title" class="text--primary">
      <v-tooltip id="chart_details_title_tooltip" activator="parent" location="top">{{
        cardTitle
      }}</v-tooltip>
      <p class="three-lines"> {{ cardTitle }} </p>
    </v-card-title>

    <div class="d-flex flex-column mb-0 height flat">
      <div id="listContainer" class="list-container">
        <analyze-data-table
          v-if="computedTableOptions.headers && computedTableOptions.items"
          table-id="details-table-id"
          fixed-header
          :headers="computedTableOptions.headers"
          :items="computedTableOptions.items"
          :has-multisort="true"
          :page="serverOptions.page"
          :items-per-page="serverOptions.rowsPerPage"
          :total-items="totalItems"
          :load-items="loadDetailsFromServer"
          :has-server-sided-pagination="true"
          @select="navigateToArtifact"
        >
        </analyze-data-table>
      </div>
    </div>
  </v-card>
  <v-card v-else class="chartdetailsmenu resizable-card-content" />
</template>

<script lang="ts" setup>
  import { ChartDetails, WidgetProperties } from '@/models/dashboard.model'
  import { Project, Snapshot } from '@/models'
  import labels from '@/translations/labels.json'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useProjectStore } from '@/stores/projectStore'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import DragHandler from '@/services/impl/DragHandler'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { useTraceDataStore } from '@/stores/traceDataStore'
  import { useRouteStore } from '@/stores/routeStore'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const props = defineProps({
    currentChartDetails: { type: Object as () => WidgetProperties, required: true },
    currentSeriesName: { type: String, required: true },
    viewIndex: { type: Number, required: true, default: 0 },
    chartIndex: { type: Number, required: true, default: 0 },
    gridItemId: { type: String, required: true },
  })

  const emit = defineEmits<{
    (event: 'closeChartDetails', value: void): void
  }>()

  const dashboardService = injectTyped(ServiceKeys.dashboard)
  const detailsHeaders = ref<AnalyzeTableHeader[]>([])
  const detailsItems = ref<any[]>([])
  const totalItems = computed(() => {
    const count = props.currentChartDetails.chart?.data?.find(
      (data) => data.name == props.currentSeriesName,
    )?.count
    return count
  })

  const router = useRouter()
  const projectStore = useProjectStore()
  const snapshotStore = useSnapshotStore()
  const traceDataStore = useTraceDataStore()
  const routeStore = useRouteStore()

  const currentProject = computed((): Project | undefined => {
    return projectStore.currentProject
  })

  const currentSnapshot = computed((): Snapshot | undefined => {
    return snapshotStore.currentSnapshot
  })

  const cardTitle = computed(() => {
    return `${props.currentChartDetails.chart?.defaultTitle}: ${props.currentSeriesName}`
  })

  const dragDetailsTarget = computed(() => {
    return document.getElementById(
      `details-header-${props.chartIndex}-${props.viewIndex}`,
    ) as HTMLElement
  })

  const dragDetailsContent = computed(() => {
    return document.getElementById(`chart-details-${props.chartIndex}-${props.viewIndex}`)
      ?.parentElement as HTMLElement
  })

  const serverOptions = ref<{ page: number; rowsPerPage: number }>({
    page: 1,
    rowsPerPage: 25,
  })

  const computedTableOptions = computed(() => {
    let headers: AnalyzeTableHeader[] = []
    let items: AnalyzeTableItem<any>[] = []

    headers = detailsHeaders.value
    items = detailsItems.value
    return { headers, items }
  })

  let dragger: DragHandler
  onMounted(() => {
    if (dragDetailsTarget.value && dragDetailsContent.value) {
      dragger = new DragHandler(
        dragDetailsTarget.value,
        dragDetailsContent.value,
        'dashboard-element',
      )

      dragger.enableDragging()

      setNewDraggerPosition()
    }
  })

  const loadDetailsFromServer = async (options: {
    page: number
    itemsPerPage: number
    sortBy?: string[]
    sortDesc?: boolean[]
  }) => {
    const { page, itemsPerPage } = options

    serverOptions.value.page = page
    serverOptions.value.rowsPerPage = itemsPerPage

    detailsItems.value = []
    detailsHeaders.value = []
    if (props.currentChartDetails.chart !== undefined && props.currentSeriesName !== '') {
      const data: ChartDetails = await dashboardService.getChartDetailsData(
        props.currentChartDetails.chart,
        props.currentSeriesName,
        serverOptions.value,
      )
      const headers = dashboardService.getChartDetailsHeaders(data)
      const items = dashboardService.getChartDetailsItems(data)
      detailsItems.value = items
      detailsHeaders.value = headers
    }
  }

  function setNewDraggerPosition(): void {
    const xb = document.getElementById(props.gridItemId)?.getBoundingClientRect().left || 200
    const x = document.getElementById(props.gridItemId)?.offsetWidth || 200
    const sidebarWidth = document.getElementById('sidebar')?.offsetWidth || 0
    const viewportWidth = document.getElementById('dashboard-element')?.offsetWidth || 0
    const cardWidth =
      document.getElementById(`chart-details-${props.chartIndex}-${props.viewIndex}`)
        ?.offsetWidth || 0

    let y = 0
    const element = document.getElementById(props.gridItemId)

    if (element !== undefined && element !== null) {
      y = +element.style.transform.split(',')[1].trim().split('px')[0]
      y += 140 // y offset to align with the chart
    }

    if (xb - sidebarWidth + x + cardWidth <= viewportWidth) {
      // top-right chart corner
      dragger.setNewPosition(xb - sidebarWidth + x, y)
    } else if (xb - sidebarWidth >= cardWidth) {
      // beside the top-left chart corner
      dragger.setNewPosition(xb - sidebarWidth - cardWidth, y)
    } else {
      // top-left chart corner
      dragger.setNewPosition(xb - sidebarWidth, y)
    }

    const dashboardHeight = document.getElementById('dashboard-element')?.offsetHeight || 1200
    ;(
      document.querySelector(
        '.v-overlay.v-overlay--absolute.v-overlay--active.v-theme--light.v-locale--is-ltr.v-menu',
      ) as HTMLElement
    ).style.height = `${dashboardHeight}px`
  }

  function closeChartDetails(): void {
    emit('closeChartDetails')
  }

  function openDetailsInNewTab(): void {
    if (props.currentChartDetails.chart) {
      const resolvedRoute = router.resolve({
        name: labels.DASHBOARD_DETAILS_VIEWER.TITLE,
        params: {
          projectId: currentProject.value?.id.toString(),
          snapshotId: currentSnapshot.value?.id.toString(),
          metricsDetailsPath: dashboardService.getMetricsDetailsPath(
            props.currentChartDetails.chart,
          ),
          seriesName: props.currentSeriesName,
          totalServerItemsLength: totalItems.value as number,
        },
      })
      window.open(resolvedRoute.href, '_blank')
    }
  }

  function navigateToArtifact(tableRow: AnalyzeTableItem<any>): void {
    const artifactId = tableRow.id as string
    if (!artifactId) return
    traceDataStore.setArtifactId(artifactId)
    router.push({
      name: labels.SIDEBAR.MAIN.EXPLORER_GRAPH,
      params: {
        projectId: currentProject.value?.id.toString(),
        snapshotId: currentSnapshot.value?.id.toString(),
        artifactId: artifactId,
      },
      query: {
        filter: routeStore.filterId,
      },
    })
    routeStore.setArtifactId(artifactId)
  }
</script>

<style lang="scss">
  .list-container {
    height: 100%;
  }
</style>

<style lang="scss" scoped>
  .text--primary {
    padding: 0%;
  }
  .three-lines {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    white-space: normal;
  }
  .chartdetailsmenu {
    padding: 15px 25px;
    overflow: hidden !important;
    width: 520px;
    height: fit-content;
    top: 54px;
  }
  .resizable-card-content {
    resize: both;
    overflow: auto;
    scrollbar-width: none;
  }

  .v-menu > .v-overlay__content > .v-card,
  .v-menu > .v-overlay__content > .v-sheet,
  .v-menu > .v-overlay__content > .v-list {
    height: 380px;
  }

  .card-header {
    font-size: 16px;
    color: #4d4d4d;
    height: 18px;
    line-height: 30px;
    text-align: left;
    position: relative;
    padding: 0 5px;
    &.center {
      text-align: center;
    }

    .right-buttons {
      height: 30px;
      float: right;
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: row;
      text-align: right;
    }
  }

  .details-header-title {
    font-weight: normal;
    font-size: 18px;
    line-height: 20px;
  }

  .details-list-item {
    font-weight: normal;
    color: black;
  }

  .artifact-name {
    color: rgb(var(--v-theme-font-color));
    font-weight: bolder;
  }
  select.select-items {
    cursor: pointer;
    font-size: 12px;
    padding: 2px 0px;
  }
  .d-flex.flex-column {
    height: calc(100% - 40px);
    .list-container {
      flex: 1;
    }
  }

  .list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
  }
</style>
